import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import type { AppLanguage } from '@anidex/shared/language';
import type { ResolvedPageMeta } from '@anidex/shared/seo';
import { api } from '../services/api';

const STATIC_META: Record<string, string> = {
  '/': 'home',
  '/regles': 'rules',
  '/conditions': 'terms',
  '/confidentialite': 'privacy',
  '/mentions-legales': 'notice',
  '/cookies': 'cookies',
  '/connexion': 'login',
  '/inscription': 'register',
  '/verifier-email': 'verify',
  '/boosters': 'boosters',
  '/purge': 'purge',
  '/collection': 'collection',
  '/animes': 'animes',
  '/quetes': 'quests',
  '/calendrier': 'calendar',
  '/moisson': 'harvest',
  '/amis': 'friends',
  '/amis/atlas': 'atlas',
  '/amis/parrainage': 'referral',
  '/messages': 'messages',
  '/notifications': 'notifications',
  '/profil': 'profile',
  '/marche': 'market',
  '/marche/encheres': 'auctions',
  '/marche/mes-ventes': 'myListings',
  '/marche/mes-encheres': 'myAuctions',
  '/marche/historique': 'history',
  '/echanges': 'trades',
};

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let el = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!el) {
    const tag = selector.startsWith('link') ? 'link' : 'meta';
    el = document.createElement(tag) as HTMLMetaElement | HTMLLinkElement;
    document.head.appendChild(el);
  }
  for (const [key, value] of Object.entries(attributes)) {
    el.setAttribute(key, value);
  }
}

function applyMeta(meta: ResolvedPageMeta) {
  document.title = meta.title;
  upsertMeta('meta[name="description"]', { name: 'description', content: meta.description });
  if (meta.keywords) upsertMeta('meta[name="keywords"]', { name: 'keywords', content: meta.keywords });
  upsertMeta('meta[name="robots"]', { name: 'robots', content: meta.robots });
  upsertMeta('link[rel="canonical"]', { rel: 'canonical', href: meta.canonical });
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: meta.locale });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: meta.title });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: meta.description });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: meta.canonical });
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: meta.image });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: meta.type });
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: meta.title });
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: meta.description });
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: meta.image });
}


export function usePageMeta() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const path = `${location.pathname}${location.search}`;
    const language: AppLanguage = i18n.language.startsWith('en') ? 'en' : 'fr';
    void api<ResolvedPageMeta>(`/seo/meta${query(path, language)}`)
      .then((meta) => {
        if (cancelled) return;
        const key = STATIC_META[location.pathname];
        applyMeta(
          key
            ? { ...meta, title: t(`meta.${key}.title`), description: t(`meta.${key}.description`) }
            : meta,
        );
      })
      .catch(() => {
        
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, i18n.language, t]);
}

function query(path: string, language: AppLanguage) {
  return `?path=${encodeURIComponent(path)}&lang=${language}`;
}
